import React from "react";
import { motion } from "framer-motion";
import { ColyMascot } from "@/components/ColyMascot";

/* iPhone frame with notch + status bar + bottom indicator. Children render the screen content. */
export const PhoneFrame = ({ children, scale = 0.62, rotate = 0, className = "", bare = false }) => {
  const W = 402;
  const H = 874;
  return (
    <motion.div
      className={`relative ${className}`}
      style={{
        width: W * scale,
        height: H * scale,
        transform: `rotate(${rotate}deg)`,
      }}
      whileHover={{ rotate: rotate * 0.5, transition: { duration: 0.6 } }}
    >
      <div
        className="absolute top-0 left-0"
        style={{
          width: W,
          height: H,
          transform: `scale(${scale})`,
          transformOrigin: "left top",
        }}
      >
        <div
          className="relative overflow-hidden bg-[#FAF6EE] font-sans antialiased"
          style={{
            width: W,
            height: H,
            borderRadius: 48,
            boxShadow: "0 40px 80px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.12)",
          }}
        >
          {/* Dynamic island */}
          <div
            className="absolute bg-black"
            style={{
              top: 11,
              left: "50%",
              transform: "translateX(-50%)",
              width: 126,
              height: 37,
              borderRadius: 24,
              zIndex: 50,
            }}
          />
          {!bare && (
            <div className="absolute top-0 left-0 right-0 z-10">
              <div className="flex items-center justify-between px-6 pt-5 pb-4 relative z-20">
                <span
                  className="text-black"
                  style={{
                    fontFamily: "-apple-system, SF Pro, system-ui",
                    fontWeight: 590,
                    fontSize: 17,
                    lineHeight: "22px",
                    paddingLeft: 12,
                  }}
                >
                  9:41
                </span>
                <div className="flex items-center gap-2 pr-2">
                  <svg width="19" height="12" viewBox="0 0 19 12">
                    <rect x="0" y="7.5" width="3.2" height="4.5" rx="0.7" fill="#000" />
                    <rect x="4.8" y="5" width="3.2" height="7" rx="0.7" fill="#000" />
                    <rect x="9.6" y="2.5" width="3.2" height="9.5" rx="0.7" fill="#000" />
                    <rect x="14.4" y="0" width="3.2" height="12" rx="0.7" fill="#000" />
                  </svg>
                  <svg width="17" height="12" viewBox="0 0 17 12">
                    <path d="M8.5 3.2C10.8 3.2 12.9 4.1 14.4 5.6L15.5 4.5C13.7 2.7 11.2 1.5 8.5 1.5C5.8 1.5 3.3 2.7 1.5 4.5L2.6 5.6C4.1 4.1 6.2 3.2 8.5 3.2Z" fill="#000" />
                    <path d="M8.5 6.8C9.9 6.8 11.1 7.3 12 8.2L13.1 7.1C11.8 5.9 10.2 5.1 8.5 5.1C6.8 5.1 5.2 5.9 3.9 7.1L5 8.2C5.9 7.3 7.1 6.8 8.5 6.8Z" fill="#000" />
                    <circle cx="8.5" cy="10.5" r="1.5" fill="#000" />
                  </svg>
                  <svg width="27" height="13" viewBox="0 0 27 13">
                    <rect x="0.5" y="0.5" width="23" height="12" rx="3.5" stroke="#000" strokeOpacity="0.35" fill="none" />
                    <rect x="2" y="2" width="20" height="9" rx="2" fill="#000" />
                  </svg>
                </div>
              </div>
            </div>
          )}
          {/* Screen body */}
          <div className="h-full w-full overflow-hidden">
            {bare ? (
              <div className="absolute inset-0">{children}</div>
            ) : (
              <div className="pt-[54px] relative h-full bg-[#FAF6EE]">{children}</div>
            )}
          </div>
          {/* Home indicator */}
          <div className="absolute bottom-0 left-0 right-0 h-[34px] flex justify-center items-end pb-2 pointer-events-none z-[60]">
            <div className="w-[139px] h-[5px] rounded-full bg-black/25" />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

/* Real screenshot image filling the phone frame */
export const ScreenshotImage = ({ src, alt = "Coly app screenshot" }) => (
  <img
    src={src}
    alt={alt}
    draggable={false}
    className="w-full h-full object-cover select-none"
  />
);

/* Real Coly app screenshots (provided by user) */
export const SCREENSHOTS = {
  diary: "https://customer-assets.emergentagent.com/job_coly-coming-soon/artifacts/cpxjo6ef_WhatsApp%20Image%202026-05-08%20at%2019.19.04%20%282%29.jpeg",
  fullCheckin: "https://customer-assets.emergentagent.com/job_coly-coming-soon/artifacts/3e2dj3gt_WhatsApp%20Image%202026-05-08%20at%2019.19.04%20%281%29.jpeg",
  bathroom: "https://customer-assets.emergentagent.com/job_coly-coming-soon/artifacts/zubk8riu_WhatsApp%20Image%202026-05-08%20at%2019.19.05.jpeg",
  painFlare: "https://customer-assets.emergentagent.com/job_coly-coming-soon/artifacts/bjdpju4s_WhatsApp%20Image%202026-05-08%20at%2019.19.04.jpeg",
  addMedication: "https://customer-assets.emergentagent.com/job_coly-coming-soon/artifacts/b4jz951s_WhatsApp%20Image%202026-05-08%20at%2019.19.05%20%281%29.jpeg",
};

/* ---------------- Insights screen ---------------- */
export const InsightsScreen = () => (
  <div className="min-h-full bg-[#FAF6EE] text-[#2F3A2C] font-sans px-[18px] pt-[18px] pb-[110px]">
    <ScreenHeader title="Insights" right="30 days" />

    {/* Trend hero card */}
    <div className="bg-white rounded-3xl p-[18px] border border-[#2F3A2C]/10 flex items-center gap-[14px]">
      <ColyMascot size={72} mood="good" withSparkles animate={false} />
      <div className="flex-1">
        <div className="text-[12px] font-extrabold text-[#7A8576] uppercase tracking-wider">Trend</div>
        <div className="text-[22px] font-black leading-tight">Pain ↓ 38% vs last month</div>
        <div className="text-[12px] text-[#7A8576] mt-0.5">Whatever you're doing — keep going.</div>
      </div>
    </div>

    {/* Pain chart */}
    <SectionTitle>Pain · last 30 days</SectionTitle>
    <Card>
      <svg viewBox="0 0 300 80" width="100%" height="80" preserveAspectRatio="none">
        <defs>
          <linearGradient id="painGrad" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#6FB089" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#6FB089" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path
          d="M0 59 L10 66 L20 52 L30 45 L40 38 L50 52 L60 59 L70 66 L80 66 L90 59 L100 52 L110 45 L120 38 L130 31 L140 45 L150 52 L160 59 L170 66 L180 66 L190 73 L200 66 L210 59 L220 52 L230 59 L240 66 L250 73 L260 73 L270 66 L280 73 L290 80 L290 80 L0 80 Z"
          fill="url(#painGrad)"
        />
        <path
          d="M0 59 L10 66 L20 52 L30 45 L40 38 L50 52 L60 59 L70 66 L80 66 L90 59 L100 52 L110 45 L120 38 L130 31 L140 45 L150 52 L160 59 L170 66 L180 66 L190 73 L200 66 L210 59 L220 52 L230 59 L240 66 L250 73 L260 73 L270 66 L280 73 L290 80"
          fill="none"
          stroke="#6FB089"
          strokeWidth="2"
          strokeLinejoin="round"
        />
        {[0, 50, 100, 150, 200, 250].map((x, i) => (
          <circle key={i} cx={x} cy={[59, 52, 52, 52, 66, 73][i]} r="3" fill="#6FB089" />
        ))}
      </svg>
      <div className="flex justify-between text-[10px] font-bold text-[#7A8576] mt-1.5">
        <span>Mar 30</span>
        <span>Apr 14</span>
        <span>Apr 28</span>
      </div>
    </Card>

    {/* Stats grid */}
    <div className="mt-4 grid grid-cols-2 gap-2.5">
      <StatCard label="Flare-up days" value="3" sub="of 30" valueColor="#E25C3D" />
      <StatCard label="Calm days" value="22" sub="of 30" valueColor="#6FB089" />
      <StatCard label="Avg pain" value="2.4" sub="↓ from 3.9" valueColor="#F2C14E" />
      <StatCard label="Med adherence" value="94%" sub="this month" valueColor="#F4A28C" />
    </div>

    {/* Pattern card */}
    <SectionTitle>Coly noticed a pattern</SectionTitle>
    <div className="bg-[#F4A28C]/20 border-[1.5px] border-[#F4A28C] rounded-3xl p-3.5 flex gap-3">
      <ColyMascot size={48} mood="meh" bodyColor="#F4A28C" withSparkles={false} animate={false} />
      <div className="flex-1">
        <div className="text-[14px] font-extrabold leading-snug">
          Pain tends to flare on days you skip evening Mesalazine.
        </div>
        <div className="text-[11px] font-bold text-[#7A8576] mt-1">
          Based on 30 days of your own data — not medical advice.
        </div>
      </div>
    </div>

    <BottomNav active="insights" />
  </div>
);

/* ---------------- Home screen ---------------- */
export const HomeScreen = () => (
  <div className="min-h-full bg-[#FAF6EE] text-[#2F3A2C] font-sans px-[18px] pt-[18px] pb-[110px]">
    <ScreenHeader title="Coly" leftIcon="settings" rightIcon="bell" />

    {/* Hi I'm Coly card */}
    <div className="rounded-[28px] p-[20px] border border-[#2F3A2C]/10 relative overflow-hidden"
      style={{ background: "linear-gradient(180deg, #F3EEE2 0%, #FFFFFF 100%)" }}>
      <div className="absolute top-2.5 right-3.5 opacity-50">
        <svg width="60" height="40" viewBox="0 0 60 40" fill="#6FB089">
          <circle cx="10" cy="8" r="2" />
          <circle cx="30" cy="20" r="1.5" />
          <circle cx="50" cy="6" r="1.8" />
        </svg>
      </div>
      <div className="flex items-center gap-3.5">
        <ColyMascot size={96} mood="quiet" withSparkles={false} animate={false} />
        <div className="flex-1">
          <div className="text-[12px] font-extrabold text-[#7A8576] uppercase tracking-wider">Tue, Apr 28</div>
          <div className="text-[22px] font-black leading-snug mt-1">Hi, I'm Coly!</div>
          <div className="text-[13px] text-[#7A8576] mt-1">How's your gut feeling?</div>
        </div>
      </div>
      {/* Level + XP */}
      <div className="mt-4 flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-xl bg-[#6FB089] text-white flex items-center justify-center font-black text-base"
          style={{ boxShadow: "0 3px 0 #4F8E6A" }}
        >
          4
        </div>
        <div className="flex-1">
          <div className="flex justify-between text-[12px] font-bold text-[#7A8576] mb-1">
            <span>Gut Guardian</span>
            <span>240 / 400 XP</span>
          </div>
          <div className="w-full h-2.5 bg-[#F3EEE2] rounded-full border border-[#2F3A2C]/10 overflow-hidden">
            <div className="h-full bg-[#6FB089] rounded-full" style={{ width: "60%" }} />
          </div>
        </div>
      </div>
    </div>

    {/* Big CTA */}
    <button
      className="mt-3.5 w-full h-14 px-5 bg-[#6FB089] text-white rounded-full font-extrabold text-[17px] inline-flex items-center justify-center gap-2"
      style={{ boxShadow: "0 4px 0 #4F8E6A, 0 4px 14px rgba(47,58,44,0.12)" }}
    >
      Log today's symptoms
      <Arrow />
    </button>

    {/* 3-stat row */}
    <div className="mt-4 grid grid-cols-[1.2fr_1fr_1fr] gap-2.5">
      <Card pad={14}>
        <div className="flex items-center gap-2">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="#F4A23C">
            <path d="M12 2c1 4 5 5 5 11a5 5 0 0 1-10 0c0-2 1-3 2-4 0 2 1 3 2 3 0-3-2-5 1-10z" />
          </svg>
          <div className="text-[24px] font-black">12</div>
        </div>
        <div className="text-[11px] font-bold text-[#7A8576] mt-0.5">day streak</div>
      </Card>
      <Card pad={14}>
        <div className="flex items-center gap-2">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="#6FB089">
            <path d="M12 21s-7-4.5-9.5-9A5.5 5.5 0 0 1 12 6a5.5 5.5 0 0 1 9.5 6c-2.5 4.5-9.5 9-9.5 9z" />
          </svg>
          <div className="text-[24px] font-black">2</div>
        </div>
        <div className="text-[11px] font-bold text-[#7A8576] mt-0.5">good days</div>
      </Card>
      <Card pad={14}>
        <div className="flex items-center gap-2">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F4A28C" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="9" width="18" height="6" rx="3" transform="rotate(-30 12 12)" />
            <path d="M9 15l6-6" />
          </svg>
          <div className="text-[24px] font-black">3/3</div>
        </div>
        <div className="text-[11px] font-bold text-[#7A8576] mt-0.5">meds today</div>
      </Card>
    </div>

    {/* Quests */}
    <div className="mt-4">
      <div className="flex items-center justify-between mb-2.5">
        <div className="font-black text-[16px] tracking-tight">Today's quests</div>
        <div className="font-bold text-[13px] text-[#4F8E6A]">See all</div>
      </div>
      <Card pad={14}>
        <Quest done text="Morning check-in" xp="+20 XP" />
        <Quest done text="Take 5-ASA" xp="+15 XP" />
        <Quest text="Drink 6 glasses of water" xp="+25 XP" progress={66.7} />
        <Quest text="Evening reflection" xp="+30 XP" last />
      </Card>
    </div>

    {/* Tip from Coly */}
    <div className="mt-4">
      <div className="font-black text-[16px] mb-2.5">Tip from Coly</div>
      <div
        className="bg-[#F4A28C] text-white rounded-3xl p-4 flex gap-3"
        style={{ boxShadow: "0 4px 0 #E07B5F" }}
      >
        <ColyMascot size={56} mood="good" bodyColor="#fff" withSparkles animate={false} />
        <div>
          <div className="text-[11px] font-extrabold uppercase tracking-wider opacity-90">Stress &amp; flares</div>
          <div className="text-[14px] font-bold mt-1 leading-snug">
            Try 4-7-8 breathing twice a day. Calmer nerves can mean a calmer gut.
          </div>
          <div className="text-[11px] opacity-85 mt-1.5">Source: Crohn's &amp; Colitis Foundation</div>
        </div>
      </div>
    </div>

    <BottomNav active="home" />
  </div>
);

/* ---------------- Reusable bits ---------------- */

const ScreenHeader = ({ title, right, leftIcon, rightIcon }) => (
  <div className="flex items-center justify-between h-12 mb-2">
    <div className="w-10">
      <button className="w-10 h-10 rounded-full bg-white border border-[#2F3A2C]/10 flex items-center justify-center">
        {leftIcon === "settings" ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2F3A2C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
          </svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2F3A2C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5" />
            <path d="M11 5l-7 7 7 7" />
          </svg>
        )}
      </button>
    </div>
    <div className="font-black text-[17px]">{title}</div>
    <div className="w-10 flex justify-end">
      {right ? (
        <div className="text-[12px] font-extrabold text-[#7A8576] px-3 py-2 bg-white rounded-full border border-[#2F3A2C]/10 whitespace-nowrap">
          {right}
        </div>
      ) : (
        <button className="w-10 h-10 rounded-full bg-white border border-[#2F3A2C]/10 flex items-center justify-center">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2F3A2C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
            <path d="M10 21a2 2 0 0 0 4 0" />
          </svg>
        </button>
      )}
    </div>
  </div>
);

const SectionTitle = ({ children }) => (
  <div className="mt-4 mb-2.5 font-black text-[16px] tracking-tight">{children}</div>
);

const Card = ({ children, pad = 16 }) => (
  <div
    className="bg-white rounded-[22px] border border-[#2F3A2C]/10"
    style={{ padding: pad, boxShadow: "0 1px 0 rgba(47,58,44,0.08), 0 6px 16px rgba(47,58,44,0.04)" }}
  >
    {children}
  </div>
);

const StatCard = ({ label, value, sub, valueColor }) => (
  <Card pad={14}>
    <div className="text-[11px] font-extrabold text-[#7A8576] uppercase tracking-wider">{label}</div>
    <div className="text-[26px] font-black leading-none mt-1" style={{ color: valueColor }}>
      {value}
    </div>
    <div className="text-[11px] font-bold text-[#7A8576] mt-0.5">{sub}</div>
  </Card>
);

const Quest = ({ done, text, xp, progress, last }) => (
  <div
    className={`flex items-center gap-3 py-2.5 ${last ? "" : "border-b border-[#2F3A2C]/10"}`}
  >
    {done ? (
      <div className="w-7 h-7 rounded-full bg-[#6FB089] flex items-center justify-center">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 13l4 4L19 7" />
        </svg>
      </div>
    ) : (
      <div className="w-7 h-7 rounded-full border-2 border-[#2F3A2C]/10" />
    )}
    <div className="flex-1">
      <div className={`text-[14px] font-bold ${done ? "text-[#7A8576] line-through" : ""}`}>
        {text}
      </div>
      {progress != null && (
        <div className="mt-1.5">
          <div className="w-full h-1.5 bg-[#F3EEE2] rounded-full border border-[#2F3A2C]/10 overflow-hidden">
            <div className="h-full bg-[#F4A28C] rounded-full" style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}
    </div>
    <div className="text-[12px] font-extrabold text-[#F4A23C]">{xp}</div>
  </div>
);

const BottomNav = ({ active }) => (
  <div
    className="absolute left-3 right-3 bottom-6 bg-white rounded-[28px] flex justify-around items-center z-30"
    style={{
      padding: "10px 6px",
      boxShadow: "0 8px 24px rgba(47,58,44,0.1), 0 0 0 1px rgba(47,58,44,0.08)",
    }}
  >
    <NavBtn label="Home" active={active === "home"} icon={<HomeIcon />} />
    <NavBtn label="Insights" active={active === "insights"} icon={<InsightsIcon />} />
    <button
      className="w-[60px] h-[60px] rounded-full bg-[#6FB089] text-white flex items-center justify-center -mt-7"
      style={{ boxShadow: "0 4px 0 #4F8E6A, 0 8px 18px rgba(47,58,44,0.18)" }}
    >
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 5v14M5 12h14" />
      </svg>
    </button>
    <NavBtn label="Community" icon={<PeopleIcon />} />
    <NavBtn label="Doctor" icon={<DocIcon />} />
  </div>
);

const NavBtn = ({ label, icon, active }) => (
  <button
    className="border-0 bg-transparent flex flex-col items-center gap-0.5 px-2 py-1 min-w-[56px]"
    style={{ color: active ? "#4F8E6A" : "#7A8576" }}
  >
    <div
      className="px-2.5 py-1 rounded-full"
      style={{ background: active ? "#F3EEE2" : "transparent" }}
    >
      {icon}
    </div>
    <span className="text-[10px] font-extrabold tracking-wide">{label}</span>
  </button>
);

const HomeIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 11l9-8 9 8" />
    <path d="M5 10v10h14V10" />
  </svg>
);
const InsightsIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 20h18" />
    <rect x="6" y="10" width="3" height="10" rx="1" />
    <rect x="11" y="6" width="3" height="14" rx="1" />
    <rect x="16" y="13" width="3" height="7" rx="1" />
  </svg>
);
const PeopleIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="9" r="3.2" />
    <circle cx="17" cy="10" r="2.4" />
    <path d="M3 19c0-3 3-5 6-5s6 2 6 5" />
    <path d="M14.5 19c0-2 2-3.5 4.5-3.5S21 17 21 19" />
  </svg>
);
const DocIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
    <path d="M14 3v6h6M9 14h6M9 17h4" />
  </svg>
);
const Arrow = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14" />
    <path d="M13 5l7 7-7 7" />
  </svg>
);
